/**
 * Stop the browser's own touch gestures from playing the game for you.
 *
 * Most of this is already done in CSS: `touch-action: none` on the body kills
 * panning and, in current engines, double-tap zoom too, and the `.panel.howto`
 * rule hands `pan-y` back to the one region that genuinely scrolls. What is left
 * here is the part CSS cannot express.
 *
 * ---------------------------------------------------------------------------
 * Why the viewport meta tag is not enough
 * ---------------------------------------------------------------------------
 * `user-scalable=no` has been ignored by Safari since iOS 10 — deliberately, as
 * an accessibility decision, and it is not coming back. Pinch zoom on iOS is
 * driven by the visual viewport rather than by the element under the fingers, so
 * `touch-action` does not reach it either. The only thing that does is
 * cancelling WebKit's proprietary `gesture*` events, which is what this does.
 *
 * ---------------------------------------------------------------------------
 * The double-tap guard, and why it measures distance
 * ---------------------------------------------------------------------------
 * A second tap soon after the first is how you ask for zoom, so the naive guard
 * is "cancel any tap within 300ms of the last one". That also swallows the click
 * from two quick taps on two *different* controls — start the game, then reach
 * straight for the `?` — because cancelling a `touchend` cancels the click the
 * browser was about to synthesise from it. A real double tap lands in the same
 * spot, so the tap has to be close in space as well as in time to count.
 *
 * Scrollable regions are exempt throughout: inside the How to Play panel a
 * double tap is the browser's own, and flicking through the rules should behave
 * the way it does anywhere else.
 */

/** Regions that keep their native scrolling, and so keep their native taps. */
const SCROLLABLE = '.panel.howto, [data-scrollable]';

/** Inside this window *and* this radius, a second tap is a zoom request. */
const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_PX = 40;

const inScrollable = (target) => target instanceof Element && !!target.closest(SCROLLABLE);

/**
 * @param {EventTarget} [target] what to listen on; the window by default.
 */
export function lockTouchGestures(target = window) {
  // WebKit pinch zoom. Not cancelable anywhere else, and absent everywhere else,
  // so this is a no-op off Safari rather than something to feature-detect.
  for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
    target.addEventListener(type, (event) => event.preventDefault(), { passive: false });
  }

  // A second finger is only ever a pinch here — nothing in the game is
  // multi-touch, so there is no gesture to be stolen by refusing one.
  target.addEventListener(
    'touchstart',
    (event) => {
      if (event.touches.length > 1 && event.cancelable) event.preventDefault();
    },
    { passive: false },
  );

  let lastTapAt = 0;
  let lastTapX = 0;
  let lastTapY = 0;

  target.addEventListener(
    'touchend',
    (event) => {
      const touch = event.changedTouches[0];
      if (!touch) return;

      const { timeStamp } = event;
      const soon = timeStamp - lastTapAt < DOUBLE_TAP_MS;
      const near = Math.hypot(touch.clientX - lastTapX, touch.clientY - lastTapY) < DOUBLE_TAP_PX;

      lastTapAt = timeStamp;
      lastTapX = touch.clientX;
      lastTapY = touch.clientY;

      if (soon && near && event.cancelable && !inScrollable(event.target)) {
        event.preventDefault();
      }
    },
    { passive: false },
  );

  // The desktop equivalent, and the tail of a double tap that got through: a
  // double click selects the word under it, which on a game board is only ever
  // an accident.
  target.addEventListener('dblclick', (event) => {
    if (!inScrollable(event.target)) event.preventDefault();
  });
}
